import React, { Fragment } from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import {
  CenteredContainer,
  Title,
  Box,
  Text,
  Divider,
  Link,
} from '@nerdwallet/currency';
import ZigZag from '../employee-branding/zig-zag/zig-zag';

import Hero from '../employee-branding/hero/hero';
import Carousel from '../employee-branding/diversity-carousel/diversity-carousel';

import carouselData from '../../lib/company-content-src/diversity-carousel-content';

const PROGRAM_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        In 2021 we rolled out the Nerd Career Accelerator Program (CAP) for
        individual contributors with an initial focus on reaching
        underrepresented Nerds. We are excited to partner with{' '}
        <Link href="http://landit.com/">Landit</Link>, an innovative technology
        platform that creates a personalized playbook for those seeking to move
        their careers forward.
      </Fragment>
    ),
  },
];

const FAIR_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        Investment in true and ongoing pay equity is essential to our values at
        NerdWallet. We’ve taken this commitment to the next level with our
        certification by{' '}
        <Link href="https://www.linkedin.com/company/fair-pay-workplace/">
          Fair Pay Workplace
        </Link>{' '}
        (FPW). Fair Pay Workplace is on a mission to eliminate pay disparities
        based on gender and race — a mission NerdWallet has supported since our
        inception in 2009 as part of our broader commitment to inclusion.
      </Fragment>
    ),
  },
];

const INCLUSIVE_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        “At NerdWallet, we aspire to foster an inclusive culture that allows all
        Nerds to grow their skills, contribute, and thrive with the confidence
        of belonging.”
      </Fragment>
    ),
  },
];

const CERTIFICATION_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        “With this certification program, we plan to pursue equity for Black
        employees across five critical pillars: black representation at every
        level, compensation equity, inclusive and anti-racist work environment,
        racially-just business practices and racial justice contributions and
        investments.” <br />
        <br /> —Lynee Luque, CPO at NerdWallet
      </Fragment>
    ),
  },
];

const LeadershipPage = () => (
  <React.Fragment>
    <div
      className={classNames([
        classes('margin-bottom-4'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <Hero
        backgroundColor="#DDFDF2"
        title="Diversity, Equity, and Inclusion Programs at NerdWallet"
        copy="We are committed to celebrating and empowering all of our colleagues in their pursuit of a life well-spent. Here’s a sampling of our initiatives:"
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-DEI-Mobile.jpg"
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-DEI-Desktop.jpg"
      />
    </div>
    <div>
      <CenteredContainer>
        <Box marginBottom="5" display="flex" justifyContent="center">
          <Title align="center" size="small">
            Employee Resource Groups
          </Title>
        </Box>
        <Box marginX="2">
          <Carousel {...carouselData} />
        </Box>
        <Box
          marginY="5"
          display="flex"
          justifyContent="center"
          marginX="5"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Text align="center" size="small" light>
            *Employee Resource Groups are voluntary, employee-led groups that
            serve as a resource for employees by fostering a diverse, inclusive
            workplace aligned with organizational mission, values, and goals.
          </Text>
        </Box>
      </CenteredContainer>
      <ZigZag
        backgroundColor="#FFF7EA"
        color="neutral-darkest"
        copyArray={FAIR_COPY}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-pay-equity.png"
        imageUrlMobile="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-pay-equity.png"
        imageAlt=""
        title="Pay Equity"
        positionImageBottom
      />
      <ZigZag
        backgroundColor="#ECF6FF"
        color="neutral-darkest"
        copyArray={PROGRAM_COPY}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_DEI-01.jpg"
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_DEI-01.jpg"
        imageAlt=""
        title="Career Growth"
        imageFirst
      />
      <CenteredContainer>
        <Box
          marginTop="5"
          marginBottom="4"
          display="flex"
          justifyContent="center"
        >
          <Title align="center" size="small">
            MLT’s Black Equity at Work
            <br /> Certification{' '}
          </Title>
        </Box>
        <Box
          marginBottom="4"
          display="flex"
          justifyContent="center"
          marginX="5"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Text align="center">
            In January of 2022, NerdWallet reached a major milestone in our
            journey to becoming certified by MLT Black Equity at Work. This
            certification will provide the clarity, guidance and support needed
            to make tangible progress towards Black equity.
          </Text>
        </Box>
      </CenteredContainer>
      <Box
        className={classNames([
          classes('display-none--wide'),
          classes('display-block'),
        ])}
      >
        <ZigZag
          backgroundColor="transparent"
          color="neutral-darkest"
          copyArray={INCLUSIVE_COPY}
          imageUrlDesktop="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-journey.png"
          imageUrlMobile="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-journey.png"
          imageAlt=""
          title=""
          imageFirst
        />
      </Box>
      <Box
        className={classNames([
          classes('display-none'),
          classes('display-block--wide'),
        ])}
      >
        <ZigZag
          backgroundColor="transparent"
          color="neutral-darkest"
          copyArray={INCLUSIVE_COPY}
          imageUrlDesktop="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-journey.png"
          imageUrlMobile="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-journey.png"
          imageAlt=""
          title=""
          positionImageBottom
        />
      </Box>
      <ZigZag
        backgroundColor="transparent"
        color="neutral-darkest"
        copyArray={CERTIFICATION_COPY}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-options.png"
        imageUrlMobile="https://www.nerdwallet.com/cdn/diversity-inclusion/dei-mlt-person-options.png"
        imageAlt=""
        title=""
        imageFirst
      />
      <CenteredContainer>
        <Box
          marginX="5"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('width-two-thirds--wide'),
          ])}
        >
          <Divider level={2} />
          <Box
            display="flex"
            marginY="5"
            paddingBottom="4"
            justifyContent="center"
          >
            <Text align="center">
              We aspire to help consumers and companies access the right
              financial products to unlock equal economic opportunity through
              our Corporate Responsibility Program which launched in 2021.
              <br />
              <br /> Our CEO and co-founder, Tim Chen was awarded{' '}
              <Link href="https://www.fastcompany.com/90651390/most-creative-people-2021-tim-chen">
                Fast Company’s Most Creative People in Business
              </Link>{' '}
              for this work. Learn more about{' '}
              <Link href="https://www.nerdwallet.com/corporate-social-responsibility?trk=nw_gf_5.0">
                the program here.
              </Link>
            </Text>
          </Box>
        </Box>
      </CenteredContainer>
    </div>
  </React.Fragment>
);

LeadershipPage.displayName = 'Leadership Page';

export default LeadershipPage;
